import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

export default function About() {
  const stats = [
    { number: "10+", label: "Years Experience" },
    { number: "500+", label: "Projects Delivered" },
    { number: "200+", label: "Happy Clients" },
    { number: "4.9/5", label: "Client Rating" }
  ];

  const values = [
    {
      title: "Innovation",
      description: "We constantly explore new technologies and methodologies to deliver cutting-edge solutions.",
      icon: "fas fa-lightbulb"
    },
    {
      title: "Quality",
      description: "Every project undergoes rigorous testing to ensure the highest standards of quality and performance.",
      icon: "fas fa-award"
    },
    {
      title: "Reliability", 
      description: "We build long-term partnerships by consistently delivering on our promises and commitments.",
      icon: "fas fa-handshake"
    },
    {
      title: "Growth",
      description: "We help businesses scale and grow by providing scalable and future-ready technology solutions.",
      icon: "fas fa-chart-line"
    }
  ];

  // Generate particles for the background
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    delay: `${Math.random() * 5}s`,
    duration: `${15 + Math.random() * 15}s`
  }));

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 overflow-x-hidden" style={{ fontFamily: "'Roboto', sans-serif" }}>
      {/* Add CSS animations in style tag */}

      <Navigation />
      
      {/* Premium Hero Section */}
<section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white py-24" style={{ fontFamily: "'Roboto', sans-serif" }}>
  {/* BACKGROUND */}
  <div className="absolute inset-0">

    {/* Aurora Glow */}
    <motion.div
      aria-hidden
      className="absolute -top-64 left-1/2 h-[700px] w-[1200px] -translate-x-1/2 rounded-full blur-3xl opacity-50"
      style={{
        background:
          "conic-gradient(from_180deg_at_50%_50%,rgba(34,211,238,0),rgba(59,130,246,0.45),rgba(99,102,241,0.35),rgba(34,211,238,0))",
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
    />

    {/* Subtle Radial Depth */}
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_70%_80%,rgba(59,130,246,0.18),transparent_60%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_20%,rgba(34,211,238,0.14),transparent_60%)]" />
  </div>

  {/* CONTENT */}
  <div className="relative z-10 container mx-auto px-6">
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: "easeOut" }}
      className="max-w-6xl mx-auto text-center"
    >
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="inline-flex items-center gap-3 px-5 py-2.5 mb-10 rounded-full 
        bg-white/10 backdrop-blur-md border border-white/15 text-cyan-300 text-sm font-medium"
      >
        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        Innovation • Technology • Growth
      </motion.div>

      {/* Headline */}
<h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-7"
  style={{ fontFamily: "'Roboto', sans-serif" }}
>
  <span className="block text-white">
    Build Software
  </span>
  <span className="block mt-3 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
    That Actually Scales
  </span>
</h1>

      {/* Subtitle */}
    <p className="text-lg lg:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10"
  style={{ fontFamily: "'Roboto', sans-serif" }}
>
  Variable Soft delivers high-quality, scalable software solutions using modern
  technologies and industry-proven development practices.
</p>


      {/* Glass Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="relative max-w-4xl mx-auto mb-16"
      >
        <div className="relative backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 lg:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: "500+", label: "Projects Delivered" },
              { value: "50+", label: "Expert Developers" },
              { value: "10+", label: "Years Experience" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent" style={{ fontFamily: "'Roboto', sans-serif" }}>
                  {stat.value}
                </div>
                <div className="mt-2 text-sm uppercase tracking-wider text-slate-400" style={{ fontFamily: "'Roboto', sans-serif" }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA */}
<motion.div
  initial={{ opacity: 0, scale: 0.95 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ delay: 0.7, duration: 0.5 }}
>
  <a href="/getintouch" target="_blank" rel="noopener noreferrer">
    <button className="relative inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-md 
      bg-gradient-to-r from-cyan-500 to-blue-600
      shadow-[0_25px_50px_-15px_rgba(59,130,246,0.8)]
      hover:shadow-[0_35px_70px_-20px_rgba(99,102,241,1)]
      transition-all duration-300 hover:scale-[1.04] active:scale-95" style={{ fontFamily: "'Roboto', sans-serif" }}>
      Start Your Project
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    </button>
  </a>
</motion.div>
    </motion.div>
  </div>

  {/* Bottom Fade */}
  <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#040814] to-transparent pointer-events-none" />
</section>

      {/* Company Story */}
<div className="force-light-mode"> {/* Added wrapper to force light mode */}
<section className="py-20 bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
  <div className="container mx-auto px-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

      {/* LEFT CONTENT */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="pl-3"
      >
        <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Roboto', sans-serif" }}>
          Smart Digital Solutions, Built for Real Business Growth
        </h2>

        <p className="text-lg text-slate-600 mb-6 leading-relaxed" style={{ fontFamily: "'Roboto', sans-serif" }}>
          Variable Soft is a technology company focused on building reliable websites,
          performance-driven digital marketing, and scalable software solutions.
          We work closely with businesses to create systems that are clear,
          efficient, and built to last.
        </p>

        <p className="text-lg text-slate-600 mb-8 leading-relaxed" style={{ fontFamily: "'Roboto', sans-serif" }}>
          Alongside our own products such as CRM, Work Hour Monitor, Dialer systems,
          and other tools, we also develop custom software tailored to your exact
          requirements — delivered with clarity, quality, and timelines you can trust.
        </p>

        <motion.a
          href="/getintouch"
          target="_blank"
          rel="noopener noreferrer"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all duration-300 shadow-lg"
            style={{ fontFamily: "'Roboto', sans-serif" }}
          >
            Contact Us
          </motion.button>
        </motion.a>

      </motion.div>

      {/* RIGHT CONTENT */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="relative"
      >
        <div className="bg-gradient-to-br from-primary to-secondary rounded-3xl p-8 text-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
          <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Roboto', sans-serif" }}>
            Why Businesses Work With Variable Soft
          </h3>

          <div className="space-y-5">
            <div className="flex items-start">
              <i className="fas fa-check-circle text-white mr-3 mt-1"></i>
              <div>
                <h4 className="font-semibold mb-1" style={{ fontFamily: "'Roboto', sans-serif" }}>End-to-End Capability</h4>
                <p className="text-white/80" style={{ fontFamily: "'Roboto', sans-serif" }}>
                  Websites, marketing, software products, and custom development —
                  handled by one experienced team.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <i className="fas fa-check-circle text-white mr-3 mt-1"></i>
              <div>
                <h4 className="font-semibold mb-1" style={{ fontFamily: "'Roboto', sans-serif" }}>Requirement-First Approach</h4>
                <p className="text-white/80" style={{ fontFamily: "'Roboto', sans-serif" }}>
                  We build what your business actually needs — no unnecessary
                  complexity or wasted effort.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <i className="fas fa-check-circle text-white mr-3 mt-1"></i>
              <div>
                <h4 className="font-semibold mb-1" style={{ fontFamily: "'Roboto', sans-serif" }}>Consistent & Dependable Delivery</h4>
                <p className="text-white/80" style={{ fontFamily: "'Roboto', sans-serif" }}>
                  Clear communication, realistic timelines, and long-term support
                  remain central to how we work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

    </div>
  </div>
</section>
</div>
      {/* Stats Section */}
<section className="relative py-24 bg-slate-950" style={{ fontFamily: "'Roboto', sans-serif" }}>
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_20%,rgba(34,211,238,0.16),transparent_60%),radial-gradient(900px_circle_at_85%_70%,rgba(99,102,241,0.14),transparent_55%),linear-gradient(to_bottom,rgba(2,6,23,0.92),rgba(2,6,23,1))]" />
  </div>

  <div className="container mx-auto px-6 relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
      className="text-center mb-16"
    >
 <h2 
  className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 md:mb-6"
  style={{ fontFamily: "'Roboto', sans-serif" }}
>
  Our Achievements
</h2>

      <p className="text-lg lg:text-xl text-slate-200 max-w-3xl mx-auto" style={{ fontFamily: "'Roboto', sans-serif" }}>
        Over the years, we've achieved significant milestones and worked with esteemed organizations.
      </p>
    </motion.div>

    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-12">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: index * 0.2 }}
          viewport={{ once: true }}
          className="text-center group relative"
        >
          <div className="overflow-hidden rounded-lg shadow-xl transition-all transform hover:scale-105 hover:shadow-2xl bg-gradient-to-r from-indigo-500 to-pink-600 group-hover:from-indigo-400 group-hover:to-pink-500">
            <div className="relative p-10 bg-slate-800 z-10">
              <h3 className="text-6xl font-extrabold text-white mb-4" style={{ fontFamily: "'Roboto', sans-serif" }}>
                {stat.number}
              </h3>
              <p className="text-slate-200 text-lg font-semibold" style={{ fontFamily: "'Roboto', sans-serif" }}>
                {stat.label}
              </p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black opacity-40 group-hover:opacity-50 transition-opacity duration-300"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300 flex items-center justify-center">
              <p className="text-white text-xl font-semibold" style={{ fontFamily: "'Roboto', sans-serif" }}>Click for More</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>


      {/* Core Values Section */}
<div className="force-light-mode"> {/* Added wrapper to force light mode */}
<section className="relative overflow-hidden py-20 bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
  {/* PREMIUM BACKGROUND */}
  <div className="absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(1100px_circle_at_20%_20%,rgba(99,102,241,0.18),transparent_60%),radial-gradient(900px_circle_at_85%_70%,rgba(34,211,238,0.14),transparent_55%),radial-gradient(900px_circle_at_50%_95%,rgba(236,72,153,0.12),transparent_60%)]" />
  </div>

  <div className="container mx-auto px-6 relative z-10">
    {/* Heading */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="text-center mb-20"
    >
      <h2 className="text-4xl lg:text-5xl font-extrabold text-blue-600 mb-6" style={{ fontFamily: "'Roboto', sans-serif" }}>
        Our Core Values
      </h2>
      <p className="text-xl text-slate-600 max-w-3xl mx-auto" style={{ fontFamily: "'Roboto', sans-serif" }}>
        These values drive everything we do and shape how we work with our clients and build solutions.
      </p>
    </motion.div>

    {/* Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
      {values.map((value, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: index * 0.12 }}
          viewport={{ once: true }}
          className="group text-center"
        >
          <div className="relative rounded-3xl p-8 bg-white/70 backdrop-blur-xl border border-white/40 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.25)] hover:shadow-[0_30px_70px_-20px_rgba(99,102,241,0.45)] transition-all duration-500">
            
            {/* Icon */}
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl mx-auto mb-6 bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-500 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <i className={`${value.icon} text-white text-2xl`} />
            </div>

            {/* Content */}
            <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Roboto', sans-serif" }}>
              {value.title}
            </h3>
            <p className="text-slate-600 leading-relaxed" style={{ fontFamily: "'Roboto', sans-serif" }}>
              {value.description}
            </p>

            {/* Subtle Hover Glow */}
            <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none bg-blue-500/10" />
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>
</div>
      {/* Call to Action */}
<section className="relative overflow-hidden bg-slate-950 py-32" style={{ fontFamily: "'Roboto', sans-serif" }}>
  {/* Animated gradient background */}
  <div className="absolute inset-0">
    {/* Aurora Clouds */}
    <motion.div
      aria-hidden
      className="absolute -top-56 left-1/2 h-[620px] w-[1200px] -translate-x-1/2 rounded-full blur-3xl opacity-40"
      style={{
        background:
          "conic-gradient(from_180deg_at_50%_50%,rgba(34,211,238,0),rgba(34,211,238,0.35),rgba(59,130,246,0.28),rgba(99,102,241,0.22),rgba(34,211,238,0))",
      }}
      animate={{ rotate: 360, y: [0, 14, 0] }}
      transition={{
        rotate: { duration: 55, repeat: Infinity, ease: "linear" },
        y: { duration: 9, repeat: Infinity, ease: "easeInOut" },
      }}
    />
    
    {/* Floating particles */}
    {[...Array(12)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute h-1 w-1 rounded-full bg-cyan-400/30"
        initial={{
          x: Math.random() * 100 + "%",
          y: Math.random() * 100 + "%",
        }}
        animate={{
          y: [null, -30, 0],
          x: [null, Math.random() * 20 - 10, 0],
        }}
        transition={{
          duration: 3 + Math.random() * 4,
          repeat: Infinity,
          delay: Math.random() * 2,
          ease: "easeInOut",
        }}
      />
    ))}
    
    {/* Grid background */}
    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:60px_60px] opacity-20" />
  </div>

  {/* Animated border glow */}
  <motion.div
    className="absolute inset-x-8 inset-y-16 rounded-3xl border border-white/5"
    animate={{
      boxShadow: [
        "0 0 60px rgba(34,211,238,0.1)",
        "0 0 90px rgba(34,211,238,0.15)",
        "0 0 60px rgba(34,211,238,0.1)",
      ],
    }}
    transition={{
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />

  <div className="container relative mx-auto px-6 text-center">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Animated badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        viewport={{ once: true }}
        className="mb-8 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 backdrop-blur-sm"
      >
        <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-400" />
        <span className="text-sm font-medium text-cyan-300" style={{ fontFamily: "'Roboto', sans-serif" }}>
          Let's Build Something Amazing
        </span>
      </motion.div>

     <h2 className="relative mb-8 text-5xl lg:text-6xl font-bold" style={{ fontFamily: "'Roboto', sans-serif" }}>
  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">
    Ready to Innovate
  </span>
  <br />
  <span className="text-white">Together?</span>
</h2>


      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
        className="mx-auto mb-12 max-w-3xl text-xl text-slate-300/80 leading-relaxed"
        style={{ fontFamily: "'Roboto', sans-serif" }}
      >
        Let's transform your vision into exceptional digital experiences. Our team is ready to 
        craft cutting-edge solutions that drive your business forward.
      </motion.p>

      <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
      {/* Primary CTA Button */}
<motion.a
  href="/getintouch"
  target="_blank"
  rel="noopener noreferrer"
>
  <motion.button
    whileHover={{ 
      scale: 1.05,
      boxShadow: "0 0 40px rgba(34,211,238,0.4)"
    }}
    whileTap={{ scale: 0.95 }}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.6 }}
    viewport={{ once: true }}
    className="group relative px-5 py-3 rounded-xl font-semibold overflow-hidden"
    style={{ fontFamily: "'Roboto', sans-serif" }}
  >
    {/* Button gradient background */}
    <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600" />
    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    
    {/* Shine effect */}
    <motion.div
      className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
      style={{
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
      }}
    />
    
    <span className="relative flex items-center gap-3 text-white">
      <span>Start Your Project</span>
      <motion.svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        animate={{ x: [0, 5, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </motion.svg>
    </span>
  </motion.button>
</motion.a>

{/* Secondary CTA Button */}
<motion.a
  href="/getintouch"
  target="_blank"
  rel="noopener noreferrer"
>
  <motion.button
    whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
    whileTap={{ scale: 0.95 }}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.7 }}
    viewport={{ once: true }}
    className="px-5 py-3 rounded-xl font-semibold border border-slate-700/50 text-white bg-white/5 backdrop-blur-sm hover:border-cyan-500/50 transition-all duration-300"
    style={{ fontFamily: "'Roboto', sans-serif" }}
  >
    <span className="flex items-center gap-3">
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
      Book a Call
    </span>
  </motion.button>
</motion.a>

      </div>

      {/* Trust indicators */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        viewport={{ once: true }}
        className="mt-16 flex flex-wrap justify-center items-center gap-8 text-sm text-slate-400"
      >
        <div className="flex items-center gap-3">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-5 w-5 rounded-full bg-yellow-500/20 border border-yellow-500/30 -ml-2 first:ml-0" />
            ))}
          </div>
          <span style={{ fontFamily: "'Roboto', sans-serif" }}>Rated 4.9/5 by 200+ Clients</span>
        </div>
        
        <div className="hidden sm:block h-4 w-px bg-slate-700" />
        
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span style={{ fontFamily: "'Roboto', sans-serif" }}>24-48 Hour Response Time</span>
        </div>
      </motion.div>
    </motion.div>
  </div>

  {/* Floating elements */}
  <motion.div
    className="absolute top-1/4 left-10 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-500/10 to-transparent blur-2xl"
    animate={{ y: [0, 30, 0] }}
    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
  />
  <motion.div
    className="absolute bottom-1/4 right-10 h-40 w-40 rounded-full bg-gradient-to-tr from-blue-500/10 to-transparent blur-2xl"
    animate={{ y: [0, -40, 0] }}
    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
  />
</section>
      <Footer />
    </div>
  );
}

